import { isAdminRole } from '@/helpers/roles';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectReportingV2Filters, setReportingV2Filters } from '@/store/slices/reportingV2/reportingV2Slice';
import { CloseRounded } from '@mui/icons-material';
import { Box, Button, Chip } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type ChannelOption, dspLabel, mergeChannelOptionsForRestrictedViewer } from '../format';
import useDspChannelMap, { DspChannelMap } from '../hooks/useDspChannelMap';
import useFilterOptions from '../hooks/useFilterOptions';
import { useReportingPermissions } from '../hooks/useReportingPermissions';
import { buildCollapsedFacetPills, countCollapsedFacetFilters } from '../lib/collapsedFacetFilters';
import { withSelectedPlatforms } from '../platformOptions';
import { COLORS } from '../theme';
import DateRangeControl from './DateRangeControl';
import GroupingControl from './GroupingControl';
import MultiSelectFilter from './MultiSelectFilter';
import PlatformChannelFilter from './PlatformChannelFilter';

// The channel filter matches the canonical channel NAME (RIBEYE.AIRBYTE.CHANNELS),
// so a chip's value must BE that name — same value space the channel donut and the
// By-Channel grouped-row click emit. (Previously these were slugs like 'ctv', which
// the backend compared against the numeric CHANNEL_ID column and 500'd.)
//
// The list is fetched rather than hardcoded, since flank#3701. A fixed nine shipped
// here, and four of them — Social, YouTube, Email, and DOOH against a dim that spells
// it "Dooh" — match no row in CHANNELS, so clicking them could only ever empty the
// report. It cut the other way too: GAM delivery resolves to "GAM Video" / "GAM
// Display", which had no chip at all, so 127M impressions were unfilterable.
//
// Fetching also satisfies the MVP criterion that an org only sees channels it
// actually delivers on: the options endpoint reads them from the fact, scoped to the
// caller.

const pillSx = {
  height: 26,
  fontSize: '0.75rem',
  bgcolor: COLORS.primarySoft,
  color: COLORS.text,
  border: `1px solid ${COLORS.primaryBorder}`,
  borderRadius: '6px',
  '& .MuiChip-label': { px: 1 },
} as const;

// Shared by both narrowing directions (flank#4627): given the selected keys on
// one axis and a key -> values map, returns the union of values those keys
// allow, or null when nothing is selected (no restriction, matches the "no
// restriction" default everywhere else in this filter bar — an empty scope
// means unscoped, not "match nothing"). An unmapped key contributes zero
// values, not every value, so selecting it narrows rather than no-ops.
const unionAcross = (selected: string[], map: DspChannelMap): Set<string> | null => {
  if (selected.length === 0) return null;
  const allowed = new Set<string>();
  for (const key of selected) for (const value of map[key] ?? []) allowed.add(value);
  return allowed;
};

/** Platform(s) selected -> channels those platforms run (narrows Channel chips). */
export const allowedChannelsFor = (dsps: string[], map: DspChannelMap): Set<string> | null => unionAcross(dsps, map);

/** Channel(s) selected -> platforms that run those channels (narrows Platform chips). */
export const allowedPlatformsFor = (channels: string[], invertedMap: DspChannelMap): Set<string> | null =>
  unionAcross(channels, invertedMap);

/**
 * Toggles a Channel chip against the current selection, resolving `ch` (the
 * chip's own value, or a raw id read back off a pill) through `channelOptions`
 * first — for a restricted viewer that value may be a merged option's
 * synthetic group value covering more than one raw channel (flank#4741
 * Problem 2: GAM's "GAM Display" masks to the same "Display" label as a true
 * Display channel). The whole group is added or removed together, so
 * selecting the merged option always includes both channels' data and there's
 * no way to pick just one half of a group the UI can't tell apart anyway.
 * Pure + exported so the merge-selection behavior is testable without
 * mounting the component (no RTL in this repo).
 */
export const toggleChannelSelection = (current: string[], ch: string, channelOptions: ChannelOption[]): string[] => {
  const group = channelOptions.find(o => o.value === ch || o.values.includes(ch))?.values ?? [ch];
  return group.some(v => current.includes(v))
    ? current.filter(c => !group.includes(c))
    : [...current, ...group.filter(v => !current.includes(v))];
};

// The backend only ships DSP -> channels (flank#4627); Channel -> Platform narrowing
// needs the same data read the other way, so it's inverted client-side rather than
// adding a second endpoint for what's already bidirectional data.
export const invertDspChannelMap = (map: DspChannelMap): DspChannelMap => {
  const inverted: DspChannelMap = {};
  for (const [dsp, channels] of Object.entries(map)) {
    for (const ch of channels) {
      (inverted[ch] ??= []).push(dsp);
    }
  }
  return inverted;
};

export type ReconciliationAxis = 'dsp' | 'channel' | null;

// Resolves BOTH axes from one consistent snapshot of state, instead of two
// independent effects each computing "what's still allowed" against the same
// pre-update state (the race CodeRabbit flagged on PR #4642: select a
// Platform and a Channel while the map is still loading, and once it
// resolves to find them mutually incompatible, both effects can fire from
// the same render and dispatch separate clears -- the reducer merges them,
// so BOTH arrays end up empty and the report falls back to completely
// unscoped). Returns null when nothing needs to change.
//
// A real mutual incompatibility -- where pruning either axis alone would
// still leave the other wanting to prune too -- is resolved by keeping
// whichever axis the user touched most recently and narrowing the OTHER axis
// down to what's compatible with it, never both. With no recent interaction
// (e.g. an incompatible pair already sitting in an initial saved-report
// state), Platform wins by default since it gates Channel visibility
// elsewhere in this bar.
export const reconcileDspChannelSelection = (
  dsps: string[],
  channelIds: string[],
  dspChannelMap: DspChannelMap,
  invertedDspChannelMap: DspChannelMap,
  lastTouchedAxis: ReconciliationAxis,
): { dsps: string[]; channelIds: string[] } | null => {
  const allowedChannelsNow = allowedChannelsFor(dsps, dspChannelMap);
  const allowedPlatformsNow = allowedPlatformsFor(channelIds, invertedDspChannelMap);

  const channelsPruned = allowedChannelsNow === null ? channelIds : channelIds.filter(c => allowedChannelsNow.has(c));
  const platformsPruned = allowedPlatformsNow === null ? dsps : dsps.filter(d => allowedPlatformsNow.has(d));

  const channelsWouldChange = channelsPruned.length !== channelIds.length;
  const platformsWouldChange = platformsPruned.length !== dsps.length;

  if (!channelsWouldChange && !platformsWouldChange) return null;

  if (channelsWouldChange && platformsWouldChange) {
    return lastTouchedAxis === 'channel' ? { dsps: platformsPruned, channelIds } : { dsps, channelIds: channelsPruned };
  }

  return {
    dsps: platformsWouldChange ? platformsPruned : dsps,
    channelIds: channelsWouldChange ? channelsPruned : channelIds,
  };
};

type FilterOptionPair = { value: string; label: string };

// Pure so it's testable without a rendered DOM (no RTL in this repo). Only fires
// when a selection actually excludes something — a map that happens to allow
// every option is not narrowing, so no note.
export const channelNarrowingNote = (
  selectedDsps: string[],
  allowedChannels: Set<string> | null,
  allChannelOptions: readonly ChannelOption[],
): string | null => {
  if (allowedChannels === null) return null;
  // A merged option (flank#4741 Problem 2) counts as allowed if ANY of the
  // raw values behind it is — it still represents real, selectable delivery.
  const allowedLabels = allChannelOptions.filter(o => o.values.some(v => allowedChannels.has(v))).map(o => o.label);
  if (allowedLabels.length >= allChannelOptions.length) return null;
  const verb = selectedDsps.length === 1 ? 'runs' : 'run';
  const selectedLabels = selectedDsps.map(dspLabel).join(', ');
  return `Channels narrowed to what ${selectedLabels} ${verb}: ${allowedLabels.length ? allowedLabels.join(', ') : 'none'}`;
};

export const platformNarrowingNote = (
  selectedChannels: string[],
  allowedPlatforms: Set<string> | null,
  allPlatformOptions: readonly FilterOptionPair[],
): string | null => {
  if (allowedPlatforms === null) return null;
  const allowedLabels = allPlatformOptions.filter(o => allowedPlatforms.has(o.value)).map(o => o.label);
  if (allowedLabels.length >= allPlatformOptions.length) return null;
  return `Platforms narrowed to ones that run ${selectedChannels.join(', ')}: ${allowedLabels.length ? allowedLabels.join(', ') : 'none'}`;
};

/**
 * Compact, sticky-pinned filter row matched to the FLANK 2.0 reference (#4626):
 * White Label (admins only, #4454) / Client / Advertiser / Campaign / Line Item /
 * Date / Group toggle / collapsed Platform & Channel button, with the active
 * Platform/Channel selections surfaced as removable pills and one Clear All.
 * Product and Publisher are no longer filters (#4109).
 */
const ReportingV2FilterBar = () => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectReportingV2Filters);
  const role = useAppSelector(state => state.me.role);
  // #4442/#4623 — a user without "Show DSP / Platform" sees no Platform chips.
  const { canViewPlatform } = useReportingPermissions();
  // Bumped on "Clear All" to remount the free-text filters and wipe the text
  // typed into their inputs (MultiSelectFilter owns that internal state) — see clearAll.
  const [clearNonce, setClearNonce] = useState(0);
  // Which axis (Platform or Channel) the user last toggled by hand -- read by
  // the reconciliation effect below to decide which selection survives when
  // the map resolves to find both mutually incompatible. A ref, not state:
  // it's read-on-dispatch bookkeeping, not something that should itself
  // trigger a render.
  const lastTouchedAxis = useRef<ReconciliationAxis>(null);

  // White Label is admin-only per #4454 — Client/Agency/Advertiser users don't
  // get the filter at all, not just a narrowed option list, so this is gated at
  // render time rather than left to the server-scoped option set the way Client
  // is below. Passing '' skips the fetch entirely for everyone else.
  const whitelabel = useFilterOptions(isAdminRole(role) ? 'whitelabel' : '');
  // Client leads row 1, matching the FLANK 2.0 reference. The option list is
  // tenancy-scoped server-side by Client.visibleTo, so this renders for everyone
  // and simply resolves to one entry for a CLIENT_USER — no role check here, and
  // no way for the control to leak an org the user cannot already see. Also
  // scoped to the selected whitelabel(s) (#4454) — otherwise an admin's Client
  // dropdown kept listing every whitelabel's clients no matter what they picked.
  const client = useFilterOptions('client', { whitelabels: filters.whitelabelIds });
  // Same whitelabel scoping as Client above — otherwise picking a whitelabel
  // narrowed Client but left Advertiser (and Campaign/Line Item downstream of
  // it) still spanning every whitelabel the admin can reach (#4572).
  // Also scoped to the selected Client(s) (bug confirmed in the 2026-10-08
  // product meeting, Daniel): Advertiser previously ignored the Client filter
  // entirely.
  const advertiser = useFilterOptions('advertiser', {
    whitelabels: filters.whitelabelIds,
    clients: filters.clientIds,
  });
  // Campaign + Line Item list ribeye entities (#4614) — one selection spans the
  // matching DSP campaigns / line items on every DSP. Each is scoped by every
  // parent above it: whitelabel → client → advertiser → campaign → line item.
  // No deferUnscopedEntity here (contrast Custom Dashboards' DSP-native
  // campaign/line_item, #4610): the ribeye dimensions are a cheap Postgres
  // lookup, not the warehouse scan that fix was guarding against.
  const entityScope = {
    whitelabels: filters.whitelabelIds,
    clients: filters.clientIds,
    advertisers: filters.advertiserIds,
  };
  const campaign = useFilterOptions('ribeye_campaign', entityScope);
  const lineItem = useFilterOptions('ribeye_line_item', {
    ...entityScope,
    ribeyeCampaignIds: filters.ribeyeCampaignIds,
  });
  // Scoped to the selected whitelabel(s) (flank#4469) — without it, dsp/channel
  // route through the batched, unscoped enqueueOptions path (no whitelabels
  // param at all), so a renamed/hidden source never resolves: the backend has
  // no single whitelabel to apply a config for (found live via Martin's
  // retest, 2026-10-19 — the exact "Admin Ribeye" case). An empty selection
  // still falls through to the cheap batched fetch (scopeParams omits empty
  // arrays), so the common unfiltered case keeps today's performance.
  const channel = useFilterOptions('channel', { whitelabels: filters.whitelabelIds });
  // Platform options come from the tenant-scoped fetch rather than a static list
  // (flank#4471) — a fixed list can't know about Neuron and excludes platforms a
  // tenant has no data for. A selected platform keeps its chip even if it drops
  // out of the fetched list, same pattern as channelOptions below.
  const dsp = useFilterOptions(canViewPlatform ? 'dsp' : '', {
    whitelabels: filters.whitelabelIds,
  });
  const platformOptions = useMemo(
    () => withSelectedPlatforms(dsp.options, filters.dsps ?? []),
    [dsp.options, filters.dsps],
  );
  // DSP -> channels it can run (flank#4627), so selecting a Platform narrows
  // which Channel chips are selectable to what that platform actually
  // supports — e.g. GAM only ever reports GAM Video/GAM Display, so Search
  // shouldn't be pickable alongside it. Every consumer below already no-ops
  // without canViewPlatform (allowedChannels -> null, platformNote gated at
  // render in PlatformChannelFilter), so skipping the fetch itself closes the
  // actual leak: the full DSP list in this response is visible in the network
  // tab regardless of what gets rendered (flank#4741 QA).
  const {
    map: dspChannelMap,
    loading: dspChannelMapLoading,
    error: dspChannelMapError,
  } = useDspChannelMap(canViewPlatform, filters.whitelabelIds);
  // Neither "still loading" nor "failed to load" should restrict anything --
  // an empty map means "these platforms support zero channels", which is a
  // real, narrowing answer, not a stand-in for "don't know yet" (CodeRabbit,
  // flank#4641).
  const dspChannelMapUnresolved = dspChannelMapLoading || dspChannelMapError;

  // A channel already chosen must keep its chip even if it drops out of the fetched
  // list — narrowing the date range can empty a channel the user is filtering on, and
  // silently removing its chip would strand the filter with no way to clear it.
  const rawChannelOptions = useMemo(() => {
    const selected = filters.channelIds ?? [];
    const known = new Set(channel.options.map(o => o.value));
    return [...channel.options, ...selected.filter(v => !known.has(v)).map(v => ({ value: v, label: v }))];
  }, [channel.options, filters.channelIds]);

  // See mergeChannelOptionsForRestrictedViewer (flank#4741 Problem 2): for a
  // restricted viewer, two raw channels that mask to the same label (e.g.
  // "Display" and GAM's "GAM Display") collapse into one selectable option
  // covering both, rather than rendering as an indistinguishable, only-one-
  // pickable duplicate.
  const channelOptions = useMemo(
    () => mergeChannelOptionsForRestrictedViewer(rawChannelOptions, canViewPlatform),
    [rawChannelOptions, canViewPlatform],
  );

  const channelLabelFor = (value: string) => channelOptions.find(o => o.values.includes(value))?.label ?? value;

  // Unresolved (loading or errored) is treated the same as "no restriction"
  // (null) rather than "nothing allowed yet" — otherwise every channel chip
  // would flash (or stay) disabled before the map has actually arrived.
  // !canViewPlatform (review, CodeRabbit, #4642): SavedReportsMenu restores a
  // saved report's filters directly into state, and a saved `dsps` selection
  // can reach a viewer without Platform visibility that way. Without this,
  // allowedChannels would narrow Channel choices by Platform values that
  // viewer isn't even allowed to see — same gate as toggleChannel/channelNote
  // above.
  const allowedChannels = useMemo(
    () => (dspChannelMapUnresolved || !canViewPlatform ? null : allowedChannelsFor(filters.dsps ?? [], dspChannelMap)),
    [canViewPlatform, filters.dsps, dspChannelMap, dspChannelMapUnresolved],
  );

  // flank#4627: the ticket requires narrowing both ways. The backend only ships
  // DSP -> channels, so the Channel -> Platform direction reads the same map
  // inverted rather than a second endpoint.
  const invertedDspChannelMap = useMemo(() => invertDspChannelMap(dspChannelMap), [dspChannelMap]);
  const allowedPlatforms = useMemo(
    () => (dspChannelMapUnresolved ? null : allowedPlatformsFor(filters.channelIds ?? [], invertedDspChannelMap)),
    [filters.channelIds, invertedDspChannelMap, dspChannelMapUnresolved],
  );

  // Reactive cascade-clear: covers the case toggleDsp/toggleChannel's own
  // immediate clear can't -- selecting a Platform AND a Channel while the map
  // is still loading is unrestricted at that instant, but once the map
  // resolves the pair can turn out incompatible. This used to be two
  // independent effects (one per axis), which raced: both could fire off the
  // same pre-resolve render, each computing "what's still allowed" against
  // the other axis's stale value, and dispatch separate clears that the
  // reducer merges -- emptying BOTH arrays and leaving the report completely
  // unscoped (CodeRabbit, flank#4642). Reconciling both axes together from
  // one snapshot, in one dispatch, removes that window.
  useEffect(() => {
    if (dspChannelMapUnresolved || !canViewPlatform) return;
    const reconciled = reconcileDspChannelSelection(
      filters.dsps ?? [],
      filters.channelIds ?? [],
      dspChannelMap,
      invertedDspChannelMap,
      lastTouchedAxis.current,
    );
    if (reconciled) dispatch(setReportingV2Filters(reconciled));
  }, [
    canViewPlatform,
    dspChannelMapUnresolved,
    filters.dsps,
    filters.channelIds,
    dspChannelMap,
    invertedDspChannelMap,
    dispatch,
  ]);

  const toggleDsp = (dsp: string) => {
    lastTouchedAxis.current = 'dsp';
    const current = filters.dsps ?? [];
    const next = current.includes(dsp) ? current.filter(d => d !== dsp) : [...current, dsp];
    // Narrowing (or clearing) the platform selection can make a previously-picked
    // channel unsupported — drop it rather than leave a selected-but-hidden
    // filter silently affecting the report (same cascade-clear pattern as
    // Advertiser -> Campaign/Line Item elsewhere in this slice). The reactive
    // effect above covers the loading-at-selection-time case this can't.
    const nextAllowed = dspChannelMapUnresolved ? null : allowedChannelsFor(next, dspChannelMap);
    const nextChannelIds =
      nextAllowed === null ? filters.channelIds : (filters.channelIds ?? []).filter(c => nextAllowed.has(c));
    dispatch(setReportingV2Filters({ dsps: next, channelIds: nextChannelIds }));
  };

  const toggleChannel = (ch: string) => {
    lastTouchedAxis.current = 'channel';
    // See toggleChannelSelection above — resolves `ch` through the merged
    // option group (flank#4741 Problem 2) before updating the selection.
    const next = toggleChannelSelection(filters.channelIds ?? [], ch, channelOptions);
    // Symmetric cascade-clear to toggleDsp above, now that Channel also narrows
    // Platform (flank#4627). Skipped without canViewPlatform (review, Lucas,
    // #4442 AC): a user who can't see Platform shouldn't have their `dsps`
    // pruned by a selection they can't even view — the Channel filter has to
    // work entirely on its own for them.
    const nextAllowed = dspChannelMapUnresolved ? null : allowedPlatformsFor(next, invertedDspChannelMap);
    const nextDsps =
      !canViewPlatform || nextAllowed === null ? filters.dsps : (filters.dsps ?? []).filter(d => nextAllowed.has(d));
    dispatch(setReportingV2Filters({ channelIds: next, dsps: nextDsps }));
  };

  // Platform/Channel are collapsed behind one button (#4626); the active count on
  // the button and the removable pills below both derive from the same two lists.
  // Platform is withheld same as the chips below it (#4442/#4623, review flank#4652):
  // a saved report can carry `dsps` from a user who could see Platform, so without
  // this a restored report shows "Platform: X" to someone who shouldn't see it.
  const collapsedDsps = canViewPlatform ? (filters.dsps ?? []) : [];
  // A merged option (flank#4741 Problem 2) can put more than one raw value
  // into filters.channelIds for what the user picked as a single "Display"
  // chip — collapse those back to one id per group here, or the count and
  // the pills below would show it twice.
  const collapsedChannelIds = useMemo(() => {
    const ids = filters.channelIds ?? [];
    if (canViewPlatform) return ids;
    const seenGroups = new Set<string>();
    const out: string[] = [];
    for (const id of ids) {
      const groupKey = channelOptions.find(o => o.values.includes(id))?.value ?? id;
      if (seenGroups.has(groupKey)) continue;
      seenGroups.add(groupKey);
      out.push(id);
    }
    return out;
  }, [filters.channelIds, channelOptions, canViewPlatform]);
  const activeFacetCount = countCollapsedFacetFilters(collapsedDsps, collapsedChannelIds);
  const facetPills = buildCollapsedFacetPills(collapsedDsps, collapsedChannelIds, dspLabel, channelLabelFor);

  // Visible narrowing notes (flank#4627 AC) — only non-null when a selection on
  // the other axis actually excludes something, so an unnarrowed row stays quiet.
  // !canViewPlatform (review, Lucas, #4442 AC): a `dsps` value can still reach
  // the store for a user without Platform visibility (e.g. a saved report),
  // and the note names platform brands — printing "Channels narrowed to what
  // GAM runs…" to someone who isn't allowed to see Platform at all.
  const channelNote = useMemo(
    () => (!canViewPlatform ? null : channelNarrowingNote(filters.dsps ?? [], allowedChannels, channelOptions)),
    [canViewPlatform, filters.dsps, allowedChannels, channelOptions],
  );
  const platformNote = useMemo(
    () => platformNarrowingNote(filters.channelIds ?? [], allowedPlatforms, platformOptions),
    [filters.channelIds, allowedPlatforms],
  );

  const clearAll = () => {
    // "Clear All" must wipe both the selected records AND the free text typed
    // into the Advertiser/Campaign/Line Item search boxes (flank#4127 Task 2).
    // Reset the fetch-side search term on each hook, then bump the nonce to
    // remount the filters so their internal input text clears too.
    whitelabel.setSearch('');
    client.setSearch('');
    advertiser.setSearch('');
    campaign.setSearch('');
    lineItem.setSearch('');
    setClearNonce(n => n + 1);
    dispatch(
      setReportingV2Filters({
        whitelabelIds: [],
        clientIds: [],
        advertiserIds: [],
        ribeyeCampaignIds: [],
        ribeyeLineItemIds: [],
        productIds: [],
        publishers: [],
        dsps: [],
        channelIds: [],
        deviceIds: [],
      }),
    );
  };

  return (
    // Hidden in print: these are interactive controls, and dropdown buttons and
    // clickable chips read as UI chrome on a printed report. The window they
    // describe is stated in the header instead.
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, displayPrint: 'none' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center' }}>
        {isAdminRole(role) && (
          <MultiSelectFilter
            key={`whitelabel-${clearNonce}`}
            label='White Label'
            options={whitelabel.options}
            loading={whitelabel.loading}
            onSearchChange={whitelabel.setSearch}
            selected={filters.whitelabelIds}
            onChange={whitelabelIds => dispatch(setReportingV2Filters({ whitelabelIds }))}
          />
        )}
        <MultiSelectFilter
          key={`client-${clearNonce}`}
          label='Client'
          options={client.options}
          loading={client.loading}
          onSearchChange={client.setSearch}
          selected={filters.clientIds}
          onChange={clientIds => dispatch(setReportingV2Filters({ clientIds }))}
        />
        <MultiSelectFilter
          key={`advertiser-${clearNonce}`}
          label='Advertiser'
          options={advertiser.options}
          loading={advertiser.loading}
          onSearchChange={advertiser.setSearch}
          selected={filters.advertiserIds}
          onChange={advertiserIds => dispatch(setReportingV2Filters({ advertiserIds }))}
        />
        <MultiSelectFilter
          key={`campaign-${clearNonce}`}
          label='Campaign'
          options={campaign.options}
          loading={campaign.loading}
          onSearchChange={campaign.setSearch}
          labelOf={campaign.labelOf}
          selected={filters.ribeyeCampaignIds}
          onChange={ribeyeCampaignIds => dispatch(setReportingV2Filters({ ribeyeCampaignIds }))}
        />
        <MultiSelectFilter
          key={`lineItem-${clearNonce}`}
          label='Line Item'
          options={lineItem.options}
          loading={lineItem.loading}
          onSearchChange={lineItem.setSearch}
          labelOf={lineItem.labelOf}
          selected={filters.ribeyeLineItemIds}
          onChange={ribeyeLineItemIds => dispatch(setReportingV2Filters({ ribeyeLineItemIds }))}
        />
        <DateRangeControl />
        <GroupingControl />
        <PlatformChannelFilter
          dspOptions={platformOptions}
          dspLoading={dsp.loading}
          channelOptions={channelOptions}
          channelLoading={channel.loading}
          canViewPlatform={canViewPlatform}
          selectedDsps={filters.dsps ?? []}
          selectedChannels={filters.channelIds ?? []}
          allowedDsps={allowedPlatforms}
          allowedChannels={allowedChannels}
          channelNote={channelNote}
          platformNote={platformNote}
          activeCount={activeFacetCount}
          onToggleDsp={toggleDsp}
          onToggleChannel={toggleChannel}
        />

        <Box sx={{ flexGrow: 1 }} />

        <Button onClick={clearAll} size='small' sx={{ textTransform: 'none', color: COLORS.muted, fontSize: '0.8rem' }}>
          Clear All
        </Button>
      </Box>

      {/* Removable Platform/Channel pills — the only thing a "Platform: Beeswax"
          style summary of what's collapsed behind the button above.
          Capped + scrollable, not left to wrap freely (review, CodeRabbit,
          #4652): this box lives inside ReportingV2View's sticky header,
          which has no height limit of its own — many selected channels on a
          narrow viewport could otherwise grow the pinned header taller than
          the viewport itself, permanently covering the report below it. */}
      {facetPills.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            alignItems: 'center',
            maxHeight: 72,
            overflowY: 'auto',
          }}
        >
          {facetPills.map(pill => (
            <Chip
              key={`${pill.dimension}-${pill.value}`}
              label={pill.label}
              size='small'
              onDelete={() => (pill.dimension === 'dsp' ? toggleDsp(pill.value) : toggleChannel(pill.value))}
              deleteIcon={<CloseRounded sx={{ fontSize: 14 }} />}
              sx={pillSx}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ReportingV2FilterBar;
